<template>
  <div class="auth-wrapper">
    <b-row class="auth-inner m-0 justify-content-center align-items-center w-100">
      <b-col
        md="6"
        lg="4"
      >
        <b-card class="mb-0 px-2 py-3">
          <div class="text-center mb-2">
            <h2 class="mb-1">
              Check your email
            </h2>
            <p class="mb-0 text-muted">
              We've sent a code to <strong>{{ email }}</strong>
            </p>
          </div>

          <ValidationObserver ref="observer">
            <b-form @submit.prevent="submitCode">
              <b-form-group
                label="Verification Code"
                label-for="verify-code"
              >
                <ValidationProvider
                  v-slot="{ errors }"
                  name="Code"
                  rules="required"
                >
                  <b-form-input
                    id="verify-code"
                    v-model="code"
                    placeholder="Enter your code"
                    :state="errors.length === 0 ? null : false"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>

              <b-button
                type="submit"
                variant="primary"
                block
                :disabled="isLoading"
              >
                <template v-if="!isLoading">
                  Verify
                </template>
                <template v-else>
                  <b-spinner
                    small
                    type="border"
                    class="mr-1"
                  />
                  Verifying...
                </template>
              </b-button>
            </b-form>
          </ValidationObserver>

          <p class="text-center mt-2">
            <span>Didn't get the code?</span>
            <b-link
              v-if="downCounter === 0"
              @click="resendCode"
            >
              Resend
            </b-link>
            <span
              v-else
              class="text-muted ml-1"
            >
              Resend in {{ Math.floor(downCounter / 60) }}:{{ downCounter % 60 < 10 ? '0' + (downCounter % 60) : downCounter % 60 }}
            </span>
          </p>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BLink,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import axios from 'axios'
import Swal from 'sweetalert2'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BLink,
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      code: '',
      email: '',
      isLoading: false,
      downCounter: 600,
      timer: null,
      expiryTime: null,
    }
  },

  mounted() {
    this.email = this.$route.query.email

    if (!this.email) {
      Swal.fire({
        icon: 'error',
        title: 'Missing email',
        text: 'Email is required. Please register again.',
        confirmButtonColor: '#EA5455',
      }).then(() => {
        this.$router.push({ name: 'register' })
      })
    } else {
      this.initializeTimer()
    }
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    initializeTimer() {
      const storageKey = `verify_code_expiry_${this.email}`
      const storedExpiry = localStorage.getItem(storageKey)

      if (storedExpiry) {
        this.expiryTime = parseInt(storedExpiry, 10)
      } else {
        // Set expiry time to 10 minutes from now
        this.expiryTime = Date.now() + 600000 // 600 seconds = 10 minutes
        localStorage.setItem(storageKey, this.expiryTime.toString())
      }

      this.updateCounter()
      this.startTimer()
    },
    updateCounter() {
      const now = Date.now()
      const remainingMs = this.expiryTime - now

      if (remainingMs <= 0) {
        this.downCounter = 0
        if (this.timer) {
          clearInterval(this.timer)
          this.timer = null
        }
      } else {
        this.downCounter = Math.ceil(remainingMs / 1000)
      }
    },
    startTimer() {
      // Update every second
      this.timer = setInterval(() => {
        this.updateCounter()
      }, 1000)
    },
    async resendCode() {
      try {
        await axios.post(
          `${process.env.VUE_APP_BASE_URL}/resend-verification-code`,
          {
            email: this.email,
          },
        )

        // Reset the timer
        const storageKey = `verify_code_expiry_${this.email}`
        this.expiryTime = Date.now() + 600000 // 10 minutes from now
        localStorage.setItem(storageKey, this.expiryTime.toString())

        if (this.timer) {
          clearInterval(this.timer)
        }
        this.updateCounter()
        this.startTimer()

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Verification code resent successfully!',
          confirmButtonColor: '#7367F0',
        })
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text:
            error.response?.data?.message
            || error.response?.data?.error
            || 'Something went wrong. Please try again.',
          confirmButtonColor: '#EA5455',
        })
      }
    },
    async submitCode() {
      this.isLoading = true
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BASE_URL}/verify-code`,
          {
            email: this.email,
            code: this.code,
          },
        )

        // Clear the timer from localStorage on successful verification
        const storageKey = `verify_code_expiry_${this.email}`
        localStorage.removeItem(storageKey)

        await Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Verification successful!',
          confirmButtonColor: '#7367F0',
        })

        this.$router.push({ name: 'login' })
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text:
            error.response?.data?.message
            || error.response?.data?.error
            || 'Invalid code or something went wrong.',
          confirmButtonColor: '#EA5455',
        })
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped>
.auth-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
